// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Inline Revenue Calculator widget for Ask LodgIQ. Rendered under an assistant
// message when the agent calls show_calculator. Submits to the token-free
// /calculate/ endpoint (chat/calculators.py) — no LLM. See the backend contract
// in lodgiq_rm_backend/ASK_LODGIQ_CALCULATOR_WIDGET.md.

import React, { useState } from "react";
import { runCalculation, runPrefill } from "../../services/wizard.service";

// AL-4 Phase 2: which calculations can be seeded from live data, and how many
// date inputs they need (1 = "for date", 2 = from/to for group nights).
const PREFILL_DATES = {
  kpi_solve: 1, rate_change_impact: 1, indices: 1, required_pickup: 1,
  overbooking: 1, group_displacement: 2, goal_seek: 2,
};

// A group stay is nights, not seasons — a mistyped year would otherwise request
// hundreds of rows.
const MAX_STAY_NIGHTS = 60;

/** Inclusive night count between two YYYY-MM-DD strings; 0 if either is unusable. */
function nightSpan(from, to) {
  const a = Date.parse(`${from}T00:00:00`);
  const b = Date.parse(`${to}T00:00:00`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.floor((b - a) / 86400000) + 1;
}

// Per-calculation form schemas. Mirror revenue_calculator's tool params
// (occupancy is 0-100). `rows` = a repeatable list input (nights / channels).
const FORMS = {
  kpi_solve: {
    title: "KPI solver",
    hint: "Enter any 2 or more — the rest are derived.",
    fields: [
      { key: "rooms_sold", label: "Rooms sold" },
      { key: "capacity", label: "Capacity (rooms)" },
      { key: "revenue", label: "Revenue", money: true },
      { key: "adr", label: "ADR", money: true },
      { key: "occ_pct", label: "Occupancy", suffix: "%" },
      { key: "revpar", label: "RevPAR", money: true },
    ],
  },
  indices: {
    title: "Market indices (MPI / ARI / RGI)",
    fields: [
      { key: "hotel_occ_pct", label: "Hotel occupancy", suffix: "%", req: true },
      { key: "benchmark_occ_pct", label: "Benchmark occupancy", suffix: "%", req: true },
      { key: "hotel_adr", label: "Hotel ADR", money: true },
      { key: "benchmark_adr", label: "Benchmark ADR", money: true },
      { key: "hotel_revpar", label: "Hotel RevPAR", money: true },
      { key: "benchmark_revpar", label: "Benchmark RevPAR", money: true },
    ],
  },
  rate_change_impact: {
    title: "Rate change impact",
    fields: [
      { key: "current_rate", label: "Current rate", money: true, req: true },
      { key: "proposed_rate", label: "Proposed rate", money: true, req: true },
      { key: "current_occ_pct", label: "Current occupancy", suffix: "%", req: true },
      { key: "capacity", label: "Capacity (rooms)" },
      { key: "elasticity", label: "Elasticity", help: "blank tests -0.5 / -1 / -1.5" },
    ],
  },
  group_displacement: {
    title: "Group displacement",
    hint: "Add one row per night with that date's forecast.",
    fields: [
      { key: "group_rooms", label: "Group rooms / night", req: true },
      { key: "group_rate", label: "Group rate", money: true, req: true },
      { key: "capacity", label: "Capacity (rooms)", req: true },
      { key: "wash_pct", label: "Wash %", suffix: "%" },
      { key: "ancillary_per_room_night", label: "Ancillary / room-night", money: true },
    ],
    rows: {
      key: "nights", label: "Nights",
      cols: [
        { key: "date", label: "Date", text: true },
        { key: "forecast_occ_pct", label: "Fcst occ %" },
        { key: "forecast_adr", label: "Fcst ADR", money: true },
      ],
    },
  },
  required_pickup: {
    title: "Required pickup",
    fields: [
      { key: "otb_rooms", label: "OTB rooms", req: true },
      { key: "target_rooms", label: "Target rooms", req: true },
      { key: "days_to_arrival", label: "Days to arrival", req: true },
      { key: "recent_pickup_per_day", label: "Recent pickup / day" },
    ],
  },
  goal_seek: {
    title: "Goal seek",
    hint: "Give a RevPAR target (or revenue + capacity + days), then fix ADR or occupancy.",
    fields: [
      { key: "target_revpar", label: "Target RevPAR", money: true },
      { key: "target_revenue", label: "Target revenue", money: true },
      { key: "capacity", label: "Capacity (rooms)" },
      { key: "days", label: "Days" },
      { key: "fixed_adr", label: "Fix ADR at", money: true },
      { key: "fixed_occ_pct", label: "Fix occupancy at", suffix: "%" },
    ],
  },
  channel_profitability: {
    title: "Channel profitability",
    hint: "Add each channel — net ADR is ranked after commission.",
    fields: [],
    rows: {
      key: "channels", label: "Channels",
      cols: [
        { key: "name", label: "Name", text: true },
        { key: "gross_adr", label: "Gross ADR", money: true },
        { key: "commission_pct", label: "Comm %" },
        { key: "other_cost_per_booking", label: "Other cost" },
        { key: "avg_los", label: "Avg LOS" },
      ],
    },
  },
  flow_through: {
    title: "Flow-through",
    fields: [
      { key: "revenue_variance", label: "Revenue variance", money: true, req: true },
      { key: "gop_variance", label: "GOP variance", money: true, req: true },
      { key: "benchmark_pct", label: "Benchmark %", suffix: "%" },
    ],
  },
  overbooking: {
    title: "Overbooking",
    fields: [
      { key: "no_show_rate_pct", label: "No-show rate", suffix: "%", req: true },
      { key: "walk_cost", label: "Walk cost", money: true, req: true },
      { key: "room_rate", label: "Room rate", money: true, req: true },
      { key: "reservations", label: "Reservations" },
      { key: "capacity", label: "Capacity (rooms)" },
    ],
  },
  variance: {
    title: "Variance",
    hint: "Single numbers, or comma-separated lists (averaged).",
    fields: [
      { key: "metric", label: "Metric", select: ["occ_pct", "adr", "revpar", "revenue", "rooms", "rate", "other"], req: true },
      { key: "current", label: "Current", list: true, req: true },
      { key: "comparison", label: "Comparison", list: true, req: true },
    ],
  },
};

const CALC_ORDER = Object.keys(FORMS);

const toNum = (v) => {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

function moneyFmt(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 });
  } catch (_) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
  }
}

// Infer display units from the result key name (backend sends plain numbers).
function fmtValue(key, val, money) {
  if (val == null) return "—";
  if (typeof val !== "number") return String(val);
  const k = key.toLowerCase();
  if (k.includes("pts") || k.endsWith("_delta_pts")) return `${val} pts`;
  if (k.endsWith("_pct") || k.includes("occ_pct")) return `${val}%`;
  if (/rate|adr|revpar|revenue|cost|impact|displaced_revenue|walk/.test(k)) return money.format(val);
  return String(val);
}

function verdictTone(v) {
  const s = String(v || "").toLowerCase();
  if (/decline|dilutive|behind|infeasible|unfavorable|leaky|inconsistent|unreachable|below fair|poor|do not/.test(s)) return "bad";
  if (/accept|feasible|on pace|favorable|healthy|solved|clearly ahead|good|target already met/.test(s)) return "good";
  if (/marginal|mixed|roughly|underdetermined|slightly|caution|needs pickup/.test(s)) return "warn";
  return "neutral";
}

const prettyKey = (k) =>
  k.replace(/_/g, " ").replace(/\bpct\b/g, "%").replace(/\badr\b/gi, "ADR")
    .replace(/\brevpar\b/gi, "RevPAR").replace(/\bmpi\b/gi, "MPI")
    .replace(/\bari\b/gi, "ARI").replace(/\brgi\b/gi, "RGI");

export default function CalculatorCard({ widget, propertyId, onInterpret }) {
  const currency = widget && widget.currency;
  const money = moneyFmt(currency);
  const initCalc = (widget && FORMS[widget.calculation]) ? widget.calculation : "kpi_solve";
  const prefill = (widget && widget.prefill) || {};

  const [calc, setCalc] = useState(initCalc);
  const [values, setValues] = useState(() => {
    const v = {};
    Object.keys(prefill).forEach((k) => { if (!Array.isArray(prefill[k])) v[k] = prefill[k]; });
    if (FORMS[initCalc].fields.some((f) => f.key === "metric") && !v.metric) v.metric = "occ_pct";
    return v;
  });
  const [rows, setRows] = useState(() => {
    const r = {};
    const f = FORMS[initCalc];
    if (f.rows) r[f.rows.key] = Array.isArray(prefill[f.rows.key]) && prefill[f.rows.key].length
      ? prefill[f.rows.key] : [{}];
    return r;
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showMath, setShowMath] = useState(false);
  // AL-4 Phase 2: live-prefill provenance. liveMeta[fieldKey] = {source, as_of,
  // edited?}; the special key "__nights" tags the group nights grid.
  const [liveMeta, setLiveMeta] = useState({});
  const [pfFrom, setPfFrom] = useState("");
  const [pfTo, setPfTo] = useState("");
  const [pfBusy, setPfBusy] = useState(false);
  // Why the live fill did or didn't happen. Previously every failure was silent.
  const [pfNote, setPfNote] = useState("");

  const form = FORMS[calc];

  const track = (event, props) => {
    try { if (window.analyze && window.analyze.track) window.analyze.track(event, props || {}); } catch (_) {}
  };

  const fetchPrefill = async (nextCalc, from, to) => {
    const need = PREFILL_DATES[nextCalc];
    setPfNote("");
    if (!need || !from || (need === 2 && !to)) return;
    // A reversed stay range produced ZERO nights and no message at all — the
    // whole grid silently stayed empty. For a stay range the intent is
    // unambiguous, so normalise it and show what was used.
    let a = from;
    let b = to;
    if (need === 2 && b && a > b) {
      [a, b] = [b, a];
      setPfFrom(a);
      setPfTo(b);
      setPfNote(`Dates were the wrong way round — using ${a} to ${b}.`);
    }
    if (need === 2 && nightSpan(a, b) > MAX_STAY_NIGHTS) {
      setPfNote(`That's over ${MAX_STAY_NIGHTS} nights — shorten the stay range.`);
      return;
    }
    setPfBusy(true);
    try {
      const dates = need === 2 ? { from_date: a, to_date: b } : { date: a };
      const data = await runPrefill(propertyId, nextCalc, dates);
      const fields = data.fields || {};
      const meta = {};
      setValues((v) => {
        const nv = { ...v };
        Object.keys(fields).forEach((k) => {
          nv[k] = fields[k].value;
          meta[k] = { source: fields[k].source, as_of: fields[k].as_of };
        });
        return nv;
      });
      if (Array.isArray(data.nights) && data.nights.length && FORMS[nextCalc].rows) {
        setRows((r) => ({ ...r, [FORMS[nextCalc].rows.key]: data.nights }));
        meta.__nights = { source: data.nights_source || "Forecast" };
        if (data.nights_note) setPfNote(data.nights_note);
      } else if (FORMS[nextCalc].rows) {
        // Silence here is what the bug felt like. Say why nothing arrived.
        setPfNote(data.nights_note
          || "No forecast for those nights — fill the rows in manually.");
      } else if (!Object.keys(fields).length) {
        setPfNote("No live data for that date — enter the values manually.");
      }
      setLiveMeta(meta);
      track("askq_calc_prefill", { calculation: nextCalc, fields: Object.keys(fields).length });
    } catch (e) {
      setPfNote((e && e.message) || "Couldn't reach live data — enter the values manually.");
    } finally {
      setPfBusy(false);
    }
  };

  const switchCalc = (next) => {
    setCalc(next);
    setResult(null);
    setError("");
    setShowMath(false);
    setLiveMeta({});
    const f = FORMS[next];
    setValues(f.fields.some((x) => x.key === "metric") ? { metric: "occ_pct" } : {});
    setRows(f.rows ? { [f.rows.key]: [{}] } : {});
    // a date is already chosen -> seed the new mode from live data too
    fetchPrefill(next, pfFrom, pfTo);
  };

  const setField = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }));
    // overriding a live-filled value flips its tag to "edited" (override-rate metric)
    setLiveMeta((m) => {
      if (!m[key] || m[key].edited) return m;
      track("askq_calc_prefill_override", { calculation: calc, field: key });
      return { ...m, [key]: { ...m[key], edited: true } };
    });
  };
  const setRowCell = (listKey, i, col, val) => {
    setRows((r) => {
      const list = (r[listKey] || []).slice();
      list[i] = { ...list[i], [col]: val };
      return { ...r, [listKey]: list };
    });
    setLiveMeta((m) => {
      if (!m.__nights || m.__nights.edited) return m;
      track("askq_calc_prefill_override", { calculation: calc, field: "nights" });
      return { ...m, __nights: { ...m.__nights, edited: true } };
    });
  };
  const addRow = (listKey) => setRows((r) => ({ ...r, [listKey]: [...(r[listKey] || []), {}] }));
  const removeRow = (listKey, i) =>
    setRows((r) => {
      const list = (r[listKey] || []).slice();
      list.splice(i, 1);
      return { ...r, [listKey]: list.length ? list : [{}] };
    });

  const buildParams = () => {
    const p = {};
    (form.fields || []).forEach((fl) => {
      const raw = values[fl.key];
      if (fl.select) { if (raw) p[fl.key] = raw; return; }
      if (fl.list) {
        if (raw == null || raw === "") return;
        const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean).map(Number).filter(Number.isFinite);
        if (parts.length === 1) p[fl.key] = parts[0];
        else if (parts.length > 1) p[fl.key] = parts;
        return;
      }
      const n = toNum(raw);
      if (n !== undefined) p[fl.key] = n;
    });
    if (form.rows) {
      const built = (rows[form.rows.key] || []).map((r) => {
        const o = {};
        form.rows.cols.forEach((c) => {
          if (c.text) { if (r[c.key]) o[c.key] = r[c.key]; }
          else { const n = toNum(r[c.key]); if (n !== undefined) o[c.key] = n; }
        });
        return o;
      }).filter((o) => Object.keys(o).length);
      if (built.length) p[form.rows.key] = built;
    }
    return p;
  };

  /** Human message for an unusable rows grid, or "" when it's fine.
   *  Without this the backend answers with its own contract text
   *  ("nights must be a non-empty list of {date?, forecast_occ_pct, ...}"),
   *  which is accurate and useless to a revenue manager. */
  const rowsProblem = () => {
    if (!form.rows) return "";
    const list = rows[form.rows.key] || [];
    const filled = list.filter((r) => r && form.rows.cols.some((c) => r[c.key] !== undefined
      && r[c.key] !== ""));
    if (!filled.length) {
      return `Add at least one ${form.rows.label.toLowerCase().replace(/s$/, "")} — `
        + "pick a stay range above to fill them from your forecast, or type them in.";
    }
    const needed = form.rows.cols.filter((c) => !c.text);
    for (let i = 0; i < filled.length; i += 1) {
      const missing = needed.filter((c) => toNum(filled[i][c.key]) === undefined);
      if (missing.length) {
        return `Row ${i + 1} needs ${missing.map((c) => c.label.toLowerCase()).join(" and ")}.`;
      }
    }
    return "";
  };

  const submit = async () => {
    const problem = rowsProblem();
    if (problem) {
      setResult(null);
      setError(problem);
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Pass the chosen scenario date(s) so the backend can attach the demand
      // model's counterpart (AL-4 Phase 3) alongside the computed result.
      const dates = pfFrom ? { date: pfFrom, ...(pfTo ? { to_date: pfTo } : {}) } : undefined;
      const data = await runCalculation(propertyId, calc, buildParams(), dates);
      setResult(data);
      setShowMath(false);
    } catch (e) {
      setResult(null);
      setError((e && e.message) || "Calculation failed.");
    } finally {
      setBusy(false);
    }
  };

  const interpret = () => {
    if (!result || !onInterpret) return;
    const parts = [`Interpret my ${form.title} result.`];
    if (result.inputs) parts.push(`Inputs: ${JSON.stringify(result.inputs)}.`);
    if (result.result) parts.push(`Result: ${JSON.stringify(result.result)}.`);
    const v = result.interpretation_hints && result.interpretation_hints.verdict;
    if (v) parts.push(`Verdict: ${v}.`);
    parts.push("Give a brief revenue-management recommendation.");
    onInterpret(parts.join(" "));
  };

  // ---- result rendering ----
  const renderResult = () => {
    if (!result) return null;
    const res = result.result || {};
    const hints = result.interpretation_hints || {};
    const scalarEntries = Object.entries(res).filter(([, v]) => typeof v !== "object" || v == null);
    const arrayEntries = Object.entries(res).filter(([, v]) => Array.isArray(v));

    const cp = result.ml_counterpart;
    return (
      <div className="wiz-calc__result">
        {hints.verdict && (
          <div className={`wiz-calc__verdict wiz-calc__verdict--${verdictTone(hints.verdict)}`}>
            {hints.verdict}
          </div>
        )}
        {/* AL-4 Phase 3: the PREDICTED counterpart to this COMPUTED result */}
        {cp && (
          <div className="wiz-calc__counterpart">
            <span className="wiz-calc__cp-badge">Prediction</span>
            <span className="wiz-calc__cp-text">
              {cp.label}
              {cp.value != null && (
                <strong> {cp.unit === "occ_pct" ? `${cp.value}%` : money.format(cp.value)}</strong>
              )}
              {cp.date ? ` for ${cp.date}` : ""}
              {cp.note ? ` — ${cp.note}` : ""}
              {cp.as_of ? ` (as of ${cp.as_of})` : ""}
            </span>
          </div>
        )}
        {scalarEntries.length > 0 && (
          <div className="wiz-calc__metrics">
            {scalarEntries.map(([k, v]) => (
              <div className="wiz-calc__metric" key={k}>
                <span className="wiz-calc__metric-label">{prettyKey(k)}</span>
                <span className="wiz-calc__metric-val">{fmtValue(k, v, money)}</span>
              </div>
            ))}
          </div>
        )}
        {arrayEntries.map(([k, arr]) => {
          const objs = arr.filter((x) => x && typeof x === "object");
          if (!objs.length) return null;
          const cols = Array.from(objs.reduce((set, o) => { Object.keys(o).forEach((c) => set.add(c)); return set; }, new Set()));
          return (
            <div className="wiz-calc__subtable" key={k}>
              <div className="wiz-calc__subtable-title">{prettyKey(k)}</div>
              <table>
                <thead><tr>{cols.map((c) => <th key={c}>{prettyKey(c)}</th>)}</tr></thead>
                <tbody>
                  {objs.map((o, i) => (
                    <tr key={i}>{cols.map((c) => <td key={c}>{fmtValue(c, o[c], money)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        {Array.isArray(result.steps) && result.steps.length > 0 && (
          <div className="wiz-calc__math">
            <button className="wiz-calc__math-toggle" onClick={() => setShowMath((s) => !s)}>
              {showMath ? "Hide the math" : "Show the math"}
            </button>
            {showMath && (
              <ol className="wiz-calc__steps">
                {result.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            )}
          </div>
        )}
        {(hints.note || hints.caveats || hints.strategy || hints.actions || hints.caution) && (
          <div className="wiz-calc__notes">
            {[hints.strategy, hints.actions, hints.note, hints.caution, hints.caveats].filter(Boolean).map((t, i) => (
              <p key={i}>{t}</p>
            ))}
          </div>
        )}
        {onInterpret && (
          <button className="wiz-calc__interpret" onClick={interpret}>Ask LodgIQ to interpret ↗</button>
        )}
      </div>
    );
  };

  // Unknown schema_version → forward-compatible fallback (per the contract).
  if (widget && widget.schema_version && widget.schema_version !== 1) {
    return (
      <div className="wiz-calc wiz-calc--fallback">
        This calculator needs a newer app version. Please refresh.
      </div>
    );
  }

  return (
    <div className="wiz-calc">
      <div className="wiz-calc__head">
        <span className="wiz-calc__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="3" width="14" height="18" rx="2.5" />
            <path d="M8 7h8" />
            <path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01" />
          </svg>
        </span>
        <span className="wiz-calc__title">Revenue calculator</span>
        {/* AL-4: the COMPUTED-tier identity — exact arithmetic, not an ML prediction */}
        <span className="wiz-calc__badge"
          title="Deterministic arithmetic from the inputs shown — same answer every time, fully auditable. For demand predictions, see your Forecast and Recommendations.">
          Exact math
        </span>
        <select className="wiz-calc__select" value={calc} onChange={(e) => switchCalc(e.target.value)}>
          {CALC_ORDER.map((k) => <option key={k} value={k}>{FORMS[k].title}</option>)}
        </select>
      </div>
      {calc === "group_displacement" && !((rows.nights || []).some((n) => n && (n.date || n.forecast_occ_pct))) && (
        <div className="wiz-calc__tip">
          Tip: ask <em>“should we take 40 rooms at $189 for Oct 12–15?”</em> and I'll fill
          this from your live forecast.
        </div>
      )}
      {form.hint && <div className="wiz-calc__hint">{form.hint}</div>}

      {/* AL-4 Phase 2: pick a date -> seed the form from live data (MEASURED/
          PREDICTED inputs feeding the COMPUTED tier, all still editable). */}
      {PREFILL_DATES[calc] && (
        <div className="wiz-calc__prefill">
          <span className="wiz-calc__flabel">
            {PREFILL_DATES[calc] === 2 ? "Fill from live data for stay" : "Fill from live data for"}
          </span>
          <input type="date" value={pfFrom}
            max={PREFILL_DATES[calc] === 2 ? (pfTo || undefined) : undefined}
            onChange={(e) => { setPfFrom(e.target.value); fetchPrefill(calc, e.target.value, pfTo); }} />
          {PREFILL_DATES[calc] === 2 && (
            <>
              <span className="wiz-calc__flabel">to</span>
              {/* min/max stop the picker offering a reversed range in the first place */}
              <input type="date" value={pfTo} min={pfFrom || undefined}
                onChange={(e) => { setPfTo(e.target.value); fetchPrefill(calc, pfFrom, e.target.value); }} />
            </>
          )}
          {pfBusy && <span className="wiz-calc__pfbusy">fetching…</span>}
          {!pfBusy && pfNote && <span className="wiz-calc__pfnote">{pfNote}</span>}
        </div>
      )}

      <div className="wiz-calc__fields">
        {form.fields.map((fl) => (
          <label className="wiz-calc__field" key={fl.key}>
            <span className="wiz-calc__flabel">
              {fl.label}{fl.req && <em>*</em>}
              {liveMeta[fl.key] && (
                <span className={`wiz-calc__live ${liveMeta[fl.key].edited ? "is-edited" : ""}`}
                  title={liveMeta[fl.key].edited
                    ? "You changed this value — the live figure is no longer used"
                    : `Live from ${liveMeta[fl.key].source}${liveMeta[fl.key].as_of ? ` · as of ${liveMeta[fl.key].as_of}` : ""}`}>
                  {liveMeta[fl.key].edited ? "edited" : `live · ${liveMeta[fl.key].source}`}
                </span>
              )}
            </span>
            {fl.select ? (
              <select value={values[fl.key] || ""} onChange={(e) => setField(fl.key, e.target.value)}>
                {fl.select.map((o) => <option key={o} value={o}>{prettyKey(o)}</option>)}
              </select>
            ) : (
              <span className="wiz-calc__inputwrap">
                <input
                  type={fl.list ? "text" : "number"}
                  inputMode={fl.list ? "text" : "decimal"}
                  placeholder={fl.list ? "e.g. 72 or 70,74,68" : ""}
                  value={values[fl.key] == null ? "" : values[fl.key]}
                  onChange={(e) => setField(fl.key, e.target.value)}
                />
                {fl.suffix && <span className="wiz-calc__suffix">{fl.suffix}</span>}
              </span>
            )}
            {fl.help && <span className="wiz-calc__help">{fl.help}</span>}
          </label>
        ))}
      </div>

      {form.rows && (
        <div className="wiz-calc__rows">
          <div className="wiz-calc__rows-title">
            {form.rows.label}
            {liveMeta.__nights && (
              <span className={`wiz-calc__live ${liveMeta.__nights.edited ? "is-edited" : ""}`}
                title={liveMeta.__nights.edited
                  ? "You changed these rows — live figures partially replaced"
                  : `Live from ${liveMeta.__nights.source}`}>
                {liveMeta.__nights.edited ? "edited" : `live · ${liveMeta.__nights.source}`}
              </span>
            )}
          </div>
          <table>
            <thead>
              <tr>{form.rows.cols.map((c) => <th key={c.key}>{c.label}</th>)}<th aria-label="remove" /></tr>
            </thead>
            <tbody>
              {(rows[form.rows.key] || []).map((r, i) => (
                <tr key={i}>
                  {form.rows.cols.map((c) => (
                    <td key={c.key}>
                      <input
                        type={c.text ? "text" : "number"}
                        value={r[c.key] == null ? "" : r[c.key]}
                        onChange={(e) => setRowCell(form.rows.key, i, c.key, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <button className="wiz-calc__rowdel" onClick={() => removeRow(form.rows.key, i)} aria-label="Remove row">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="wiz-calc__addrow" onClick={() => addRow(form.rows.key)}>+ Add {form.rows.label.toLowerCase()}</button>
        </div>
      )}

      <div className="wiz-calc__actions">
        <button className="wiz-calc__run" onClick={submit} disabled={busy}>
          {busy ? "Calculating…" : "Calculate"}
        </button>
      </div>

      {error && <div className="wiz-calc__error">{error}</div>}
      {renderResult()}

      <div className="wiz-calc__foot">
        <span>Exact math from your inputs — not a demand prediction</span>
        <span>© {new Date().getFullYear()} LodgIQ</span>
      </div>
    </div>
  );
}
